import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Table2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ErrorState } from '@/components/feedback/States'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/Skeleton'
import { cn } from '@/utils/cn'
import { sectionMonthQuery } from '../api'
import { formatLongDay, formatShortDay, monthGrid, parseIso, weekdaysMondayFirst } from '../dates'
import { formatRate } from '../status'
import type { StatusCounts } from '../types'
import { MonthNav } from './MonthNav'
import { SummaryChips } from './SummaryChips'

/** Sequential single-hue scale (light = lower present rate). */
const BUCKETS = [
  { min: 97, label: '97%+', className: 'bg-brand-700 text-white' },
  { min: 93, label: '93–97%', className: 'bg-brand-500 text-white' },
  { min: 88, label: '88–93%', className: 'bg-brand-300 text-brand-950' },
  { min: 80, label: '80–88%', className: 'bg-brand-200 text-brand-950' },
  { min: 0, label: 'Below 80%', className: 'bg-brand-50 text-brand-900 ring-1 ring-inset ring-brand-200' },
] as const

const bucketFor = (rate: number) => BUCKETS.find((b) => rate >= b.min) ?? BUCKETS[4]

interface SectionMonthHeatmapProps {
  sectionId: number
  month: string
  onMonthChange: (month: string) => void
  /** Link target for a working day (e.g. the register on that date). */
  dayHref?: (date: string) => string
}

/** Month calendar of a section's daily present rate, with a table alternative. */
export function SectionMonthHeatmap({ sectionId, month, onMonthChange, dayHref }: SectionMonthHeatmapProps) {
  const { t } = useTranslation('attendance')
  const query = useQuery(sectionMonthQuery(sectionId, month))
  const [asTable, setAsTable] = useState(false)
  const data = query.data

  const byDate = new Map<string, StatusCounts>((data?.days ?? []).map((d) => [d.date, d.summary]))
  const holidays = new Map((data?.holidays ?? []).map((h) => [h.date, h]))
  const closed = new Set(data?.closedWeekdays ?? [])

  const describe = (date: string) => {
    const counts = byDate.get(date)
    const holiday = holidays.get(date)
    const future = data ? date > data.today : false
    if (holiday) return { kind: 'holiday' as const, text: t('Holiday: {{name}}', { name: holiday.name }) }
    if (closed.has(parseIso(date).getDay())) return { kind: 'closed' as const, text: t('School closed') }
    if (counts?.rate !== null && counts?.rate !== undefined)
      return {
        kind: 'rate' as const,
        counts,
        rate: counts.rate,
        text: t('{{rate}} present ({{present}} of {{marked}} marked)', {
          rate: formatRate(counts.rate),
          present: counts.present + counts.late,
          marked: counts.marked,
        }),
      }
    if (future) return { kind: 'future' as const, text: t('Upcoming') }
    return { kind: 'unmarked' as const, text: t('Not marked') }
  }

  return (
    <Card>
      <CardHeader
        title={t('Monthly attendance')}
        description={
          data
            ? `${data.section.name} · ${t('{{count}} students', { count: data.enrolled })} · ${t(
                '{{marked}} of {{count}} working days marked',
                { marked: data.days.length, count: data.workingDays },
              )}`
            : t('Daily present rate')
        }
        icon={<CalendarDays />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MonthNav month={month} onChange={onMonthChange} max={data?.today.slice(0, 7)} />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={asTable ? <CalendarDays /> : <Table2 />}
              onClick={() => setAsTable((v) => !v)}
              aria-pressed={asTable}
            >
              {asTable ? t('Calendar') : t('Table')}
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-4">
        {query.isPending ? (
          <Skeleton className="h-72 w-full" />
        ) : query.error || !data ? (
          <ErrorState compact onRetry={() => void query.refetch()} />
        ) : asTable ? (
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">
                {t('Daily attendance of {{section}}', { section: data.section.name })}
              </caption>
              <thead>
                <tr className="text-left text-2xs tracking-wider text-muted uppercase">
                  <th scope="col" className="py-2 pr-4">
                    {t('Day')}
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    {t('Present')}
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    {t('Absent')}
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    {t('Late')}
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    {t('Excused')}
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    {t('Half day')}
                  </th>
                  <th scope="col" className="py-2 text-right">
                    {t('Rate')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {data.days.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-muted">
                      {t('No attendance marked this month.')}
                    </td>
                  </tr>
                ) : (
                  data.days.map((d) => (
                    <tr key={d.date} className="text-ink-soft tabular">
                      <th scope="row" className="py-2 pr-4 text-left font-medium text-ink">
                        {formatShortDay(d.date)}
                      </th>
                      <td className="py-2 pr-4 text-right">{d.summary.present}</td>
                      <td className="py-2 pr-4 text-right">{d.summary.absent}</td>
                      <td className="py-2 pr-4 text-right">{d.summary.late}</td>
                      <td className="py-2 pr-4 text-right">{d.summary.excused}</td>
                      <td className="py-2 pr-4 text-right">{d.summary.halfDay}</td>
                      <td className="py-2 text-right font-semibold text-ink">{formatRate(d.summary.rate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <table className="w-full table-fixed border-separate border-spacing-1">
            <caption className="sr-only">
              {t('Present rate per day, {{section}}. Use the Table view for exact counts.', {
                section: data.section.name,
              })}
            </caption>
            <thead>
              <tr>
                {weekdaysMondayFirst().map((w) => (
                  <th
                    key={w.short}
                    scope="col"
                    className="pb-1 text-2xs font-semibold tracking-wider text-muted uppercase"
                  >
                    <abbr title={w.long} className="no-underline">
                      {w.short}
                    </abbr>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monthGrid(month).map((week, i) => (
                <tr key={i}>
                  {week.map((date, j) => {
                    if (!date) return <td key={j} />
                    const info = describe(date)
                    const dayNumber = Number(date.slice(8))
                    const label = `${formatLongDay(date)}: ${info.text}`
                    const content = (
                      <>
                        <span className="text-xs font-medium">{dayNumber}</span>
                        <span className="hidden text-2xs tabular sm:block">
                          {info.kind === 'rate'
                            ? formatRate(info.rate)
                            : info.kind === 'holiday'
                              ? t('Holiday')
                              : ''}
                        </span>
                      </>
                    )
                    const cellClass = cn(
                      'flex h-12 flex-col items-center justify-center rounded-xs sm:h-14',
                      info.kind === 'rate' && bucketFor(info.rate).className,
                      (info.kind === 'closed' || info.kind === 'holiday') && 'bg-surface-sunken text-subtle',
                      info.kind === 'unmarked' && 'border border-dashed border-line-strong text-muted',
                      info.kind === 'future' && 'text-subtle',
                      date === data.today && 'ring-2 ring-focus ring-offset-1 ring-offset-surface',
                    )
                    const linkable = dayHref && (info.kind === 'rate' || info.kind === 'unmarked')
                    return (
                      <td key={j} className="p-0">
                        {linkable ? (
                          <Link
                            to={dayHref(date)}
                            aria-label={t('{{label}}. Open register', { label })}
                            title={info.text}
                            className={cn(cellClass, 'transition-opacity hover:opacity-85')}
                          >
                            {content}
                          </Link>
                        ) : (
                          <div className={cellClass} title={info.text} aria-label={label} role="img">
                            {content}
                          </div>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && !asTable ? (
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted"
            aria-label={t('Legend')}
          >
            {BUCKETS.map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5">
                <span className={cn('size-3 rounded-xs', b.className)} aria-hidden="true" />
                {t(b.label)}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span
                className="size-3 rounded-xs border border-dashed border-line-strong"
                aria-hidden="true"
              />
              {t('Not marked')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="size-3 rounded-xs bg-surface-sunken" aria-hidden="true" />
              {t('Closed / holiday')}
            </span>
          </div>
        ) : null}
        {data ? <SummaryChips counts={data.summary} /> : null}
      </CardBody>
    </Card>
  )
}
